import React from "react";
import styled from "styled-components";

const Container = styled.div`
  padding: 48px 24px;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  background-color: #f9f9f9;
  min-height: 100vh;
  overflow-y: auto;

  @media (max-width: 768px) {
    padding: 10px 8px; // Reduced horizontal padding on mobile
  }
`;

const Content = styled.div`
  max-width: 900px;
  background-color: white;
  padding: 40px;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  font-family: "Segoe UI", sans-serif;
  font-size: 16px;
  line-height: 1.8;
  color: #2c2c2c;

  h2 {
    font-size: 28px;
    margin-bottom: 24px;
    padding: 0 10px;
    text-align: center;
    color: #084852;
  }

  h3 {
    font-size: 20px;
    margin-top: 32px;
    margin-bottom: 12px;
    color: #084852;
  }

  ul {
    padding-left: 20px;
    margin-top: 8px;

    li {
      margin-bottom: 10px;
    }
  }

  p {
    margin-bottom: 16px;
  }

  @media (max-width: 768px) {
    padding: 24px 16px;
  }
`;

const PrivacyPolicyPage = () => {
  return (
    <Container>
      <Content>
        <h2>Privacy Policy and Terms of Use</h2>

        {/* ───────────────────────── PRIVACY POLICY ───────────────────────── */}
        <h3>
          <strong>PulseRank Privacy Policy</strong>
        </h3>
        <p>
          <strong>Effective Date:</strong> March 2026
        </p>

        <p>
          PulseRank ("we," "us," or "our") is committed to protecting your
          privacy and complying with Indian law, including the Information
          Technology Act 2000 and the Digital Personal Data Protection Act 2023
          (DPDP Act). We will clearly explain what personal and health data we
          collect, how we use it, and how we protect it. This Privacy Policy
          applies to all PulseRank mobile and web services (collectively, "the
          App" or "Services"). By using PulseRank, you consent to the data
          practices described herein.
        </p>

        <h3>
          <strong>Information We Collect and Use</strong>
        </h3>
        <ul>
          <li>
            <strong>Personal Data:</strong> We collect basic identity and
            contact information you provide, such as name, age (or date of
            birth), gender, email address, phone number, and postal address.
          </li>
          <li>
            <strong>Account and Usage Data:</strong> We collect your username
            and password, device identifiers (model, operating system), IP
            address, and log information about how you use the App (pages
            viewed, activity logs, etc.). This includes any profile data (e.g.
            username, profile picture) you add.
          </li>
          <li>
            <strong>Health and Fitness Data:</strong> With your consent,
            PulseRank collects fitness and health-related information you enter
            or generate in the App, including height, weight, workout data,
            steps, distance, calories burned and fitness goals. This may also
            include data imported from wearable devices or fitness trackers.
          </li>
          <li>
            <strong>Sensitive Personal Data:</strong> Health and biometric data
            are considered sensitive personal data. We collect such sensitive
            information only when you explicitly provide it, and only as
            necessary for the App's functionality. Sensitive health data will
            not be collected or used without your clear, informed consent.
          </li>
        </ul>

        <p>
          <strong>Purpose of Collection:</strong> We use your personal and
          health data to operate and improve the App and to provide you with
          personalized metrics. This includes enabling account registration and
          login; providing health recommendations; tracking your progress;
          enabling social or sharing features (with your consent); communicating
          with you (e.g. updates, promotions); and tailoring the user
          experience. Data may also be used for internal analytics, research,
          and development to improve PulseRank. We will not use your data for
          purposes unrelated to these services without additional consent. All
          data collection follows the requirement to obtain explicit consent
          where needed and to collect only data that is necessary for lawful
          purposes.
        </p>

        <h3>
          <strong>Third-Party Integrations</strong>
        </h3>
        <p>
          PulseRank integrates with platform-level health data providers such as
          Health Connect on Android and Apple HealthKit on iOS to enhance your
          in-app experience through personalized health and fitness insights. We
          do not directly rely on or connect with third-party services like
          Google Fit or other fitness or nutrition apps. Any data accessed
          through Health Connect or HealthKit is subject to your explicit
          consent, and is used solely for features that benefit you within the
          PulseRank app. We strictly adhere to the privacy policies and usage
          guidelines of these platforms, ensuring that your health data is never
          shared with unauthorized third parties and is not used beyond the
          scope of your chosen permissions.
        </p>

        <h3>
          <strong>Data Security</strong>
        </h3>
        {/* v3_1 difference: "reasonable security practices" is bolded inline */}
        <p>
          We implement <strong>reasonable security practices</strong> to protect
          your data in accordance with the IT Act and its rules. Our security
          measures include encryption of data in transit (e.g. TLS/SSL), secure
          servers, network firewalls, access controls, and routine security
          audits. Our data security program follows industry standards (for
          example, ISO/IEC 27001 and other recognized frameworks). In the event
          of any data breach, we have procedures to contain and report the
          incident as required by law. However, no system is perfectly secure;
          by using PulseRank, you acknowledge that we cannot guarantee absolute
          prevention of all unauthorized access.
        </p>

        <h3>
          <strong>Data Retention</strong>
        </h3>
        {/* v3_1 difference: split into two separate paragraphs */}
        <p>
          We retain personal and health data only for as long as necessary to
          provide services, comply with legal obligations, resolve disputes, and
          enforce agreements. When data is no longer required, it will be
          securely deleted or anonymized.
        </p>
        <p>
          Users may request deletion of their personal data or withdraw consent
          for processing at any time. PulseRank provides options within the app
          to withdraw consent and delete stored health data. Users may also
          request deletion of their data by contacting us at{" "}
          <a href="mailto:pulserank@eminds.ai">pulserank@eminds.ai</a>. Upon
          receiving a valid request, we will delete or anonymize the relevant
          data within a reasonable time unless retention is required by law.
        </p>

        <h3>
          <strong>Rewards and Incentive Programs</strong>
        </h3>
        <p>
          PulseRank may provide reward-based programs where users earn
          incentives based on activity, participation, or engagement metrics.
          Data used for reward calculation may include activity logs, fitness
          metrics, or participation records.
        </p>

        <h3>
          <strong>Not medical advice</strong>
        </h3>
        <p>
          PulseRank provides general fitness and wellness insights and does not
          provide medical advice. Users should consult qualified healthcare
          professionals before making health decisions.
        </p>

        <h3>
          <strong>User Rights</strong>
        </h3>
        <p>
          You have rights regarding your personal data under the DPDP Act and IT
          Act. These include:
        </p>
        <ul>
          <li>
            <strong>Right of Access:</strong> You may request information about
            what personal data we hold about you, why it is being processed, and
            to whom it has been disclosed.
          </li>
          <li>
            <strong>Right to Correction:</strong> If any of your personal
            information is inaccurate or outdated, you can ask us to correct or
            update it.
          </li>
          <li>
            <strong>Right to Erasure:</strong> You may ask us to delete your
            personal data when it is no longer needed for the original purpose.
            We will erase data upon request unless we are legally required to
            retain it.
          </li>
          <li>
            <strong>Right to Withdraw Consent:</strong> If we rely on your
            consent to process any data (especially sensitive health data), you
            can withdraw your consent at any time. Withdrawal is as easy as the
            method used to give consent. Once consent is withdrawn, we will stop
            further processing of the affected data and delete it upon request
            if possible.
          </li>
          <li>
            <strong>Right to Nominate:</strong> You may designate a trusted
            person to exercise these rights on your behalf in the event of death
            or incapacity.
          </li>
          <li>
            <strong>Right to Grievance Redressal:</strong> You can file a
            complaint or grievance if you believe your data is mishandled. We
            have a grievance redressal mechanism in place, and we will respond
            to complaints within a reasonable time (generally within one month).
          </li>
        </ul>
        <p>
          To exercise any of these rights, please contact our Data Protection
          Officer or Grievance Officer (see Contact below). We will verify your
          identity and respond as required by law.
        </p>

        <h3>
          <strong>Children's Privacy</strong>
        </h3>
        {/* v3_1 difference: "not knowingly collect personal data from children under 18 without verifiable parental consent" is bolded */}
        <p>
          PulseRank is intended for users aged 18 and above. Users under 18 may
          only use the App with verifiable parental consent. A "child" under
          DPDP means an individual who has not completed 18 years of age. We do{" "}
          <strong>
            not knowingly collect personal data from children under 18 without
            verifiable parental consent
          </strong>
          . Parents or guardians must consent before any personal data of a
          minor is processed. Under no circumstances will we target advertising
          or track a child's data for behavioral profiling. If we discover we
          have inadvertently collected data from a child without parental
          consent, we will delete it immediately.
        </p>

        <h3>
          <strong>International Data Transfers</strong>
        </h3>
        <p>
          Since we operate in India, your data is primarily stored and processed
          within India. However, to provide global services (for example, cloud
          hosting or third-party analytics), we may transfer data
          internationally. Any cross-border transfer of personal data will
          comply with applicable Indian law and any government-notified
          restrictions. Where required, such transfers will be based on your
          consent or will be necessary for the performance of our contract with
          you. We will also comply with any additional conditions prescribed by
          the Government of India regarding transfers outside India.
        </p>

        <h3>
          <strong>Contact and Grievance Officer</strong>
        </h3>
        <p>
          For any queries or complaints regarding your data, please contact our
          Grievance Officer:
        </p>
        {/* v3_1 difference: contact details rendered as a bullet list */}
        <ul>
          <li>
            <strong>Name:</strong> Hanumanta Poreddy
          </li>
          <li>
            <strong>Email:</strong>{" "}
            <a href="mailto:hanumantha@eminds.ai">hanumantha@eminds.ai</a>
          </li>
          <li>
            <strong>Address:</strong> 12667 Alcosta Boulevard, Suite 410, San
            Ramon, CA 94583
          </li>
        </ul>
        <p>
          Pursuant to Indian law, we have designated a Grievance Officer and
          published their contact information. The Grievance Officer will
          acknowledge receipt of any privacy complaint promptly and attempt to
          resolve it within one month of receipt. If you are not satisfied with
          our response, you may approach the Data Protection Board or other
          regulatory authorities.
        </p>

        {/* v3_1 difference: "Changes to this Policy" is an inline bold label, not a heading */}
        <p>
          <strong>Changes to this Policy:</strong> We may update this Privacy
          Policy from time to time. Any changes will be posted here with a
          revised "Last Updated" date. We may also notify you by email or an
          in-app notice for material changes.
        </p>

        {/* ───────────────────────── TERMS & CONDITIONS ───────────────────────── */}
        <h3>
          <strong>PulseRank Terms and Conditions</strong>
        </h3>
        <p>
          These Terms and Conditions ("Terms") govern your use of the PulseRank
          mobile application and related services ("Services"). By downloading,
          accessing, or using PulseRank, you agree to these Terms. If you do not
          agree, you should not use the App.
        </p>

        <h3>
          <strong>Eligibility</strong>
        </h3>
        <p>
          PulseRank is available to users of all ages. However, to form a
          binding agreement, you must have the legal right and capacity to do
          so. If you are under 18, you may use the App only with the consent of
          a parent or guardian, who must agree to these Terms on your behalf.
          When you register, you warrant that all information you provide (such
          as your name, email, and other data) is accurate and complete, and
          that you are legally eligible to use this App. You agree to comply
          with all applicable laws in India and any other jurisdiction when
          using PulseRank. We are not responsible for any misinformation you
          provide or if you register unlawfully.
        </p>

        <h3>
          <strong>User Responsibilities and Acceptable Use</strong>
        </h3>
        {/* v3_1 difference: "not to" is bolded inline */}
        <p>
          You agree to use PulseRank only for lawful purposes. You will not
          misuse the Services or any content in violation of these Terms.
          Specifically, you agree <strong>not to</strong>:
        </p>
        <ul>
          <li>
            Post or transmit any content that is defamatory, obscene, offensive,
            pornographic, hateful, harassing, infringing privacy, or otherwise
            objectionable. You also agree not to encourage money laundering,
            gambling, terrorism, or any illegal activity.
          </li>
          <li>
            Violate any applicable laws (including the Indian Penal Code, IT Act
            2000, SPDI Rules 2011, and other regulations) through your use of
            the App.
          </li>
          <li>
            Infringe on any third party's rights, including intellectual
            property rights, privacy, or publicity rights.
          </li>
          <li>
            Upload viruses, malware, or any code that could damage or interfere
            with the App or other users' devices.
          </li>
          <li>
            Impersonate any person or misrepresent your identity or affiliation.
          </li>
          <li>
            Engage in activities that are known by you to be false, misleading,
            or likely to harm others.
          </li>
        </ul>
        <p>
          Additionally, you may not reverse-engineer, decompile, or disassemble
          any part of the App, or copy the App's code, except to the extent
          permitted by law. You may not rent, lease, sell, distribute, or
          sublicense the App to others. You agree to log out at the end of each
          session and safeguard your account's credentials. You are responsible
          for any activity under your account. If you believe your credentials
          have been compromised, notify us immediately.
        </p>
        <p>
          We reserve the right to investigate and take appropriate action
          against any violation of these Terms, including suspending or
          terminating your account and reporting you to law enforcement.
        </p>

        <h3>
          <strong>Subscription and Payment (If Applicable)</strong>
        </h3>
        <p>
          PulseRank may offer paid subscription plans for premium features. If
          you choose to subscribe, you agree to pay the subscription fees and
          taxes described at the time of purchase. Payments are processed
          through third-party payment providers (e.g. credit/debit cards, UPI,
          etc.) and are subject to their terms. All payment information you
          provide will be encrypted and handled securely. Subscription fees
          (once paid) are generally non-refundable, except as required by law or
          as described in our refund policy. We may change our pricing or
          payment terms for future subscriptions, but we will not increase fees
          retroactively for already-purchased subscriptions without your
          consent.
        </p>

        <h3>
          <strong>Termination</strong>
        </h3>
        <p>
          You may stop using the App at any time, and you may delete your
          account if you wish. PulseRank also reserves the right to suspend or
          terminate your access, in whole or in part, with or without notice, if
          you breach these Terms or any laws, or for any other reason. Upon
          termination, your right to use the Services will end immediately. We
          may delete your account and any content or data associated with it at
          our discretion.
        </p>
        <p>
          Any provisions of these Terms that by their nature should survive
          termination (such as liability disclaimers and governing law) will
          continue to apply.
        </p>

        <h3>
          <strong>Limitation of Liability</strong>
        </h3>
        {/* v3_1 difference: "Disclaimer of Warranties", "No Liability for Damages", "Indemnification"
            are bold inline labels within paragraphs, not separate h3 headings */}
        <p>
          <strong>Disclaimer of Warranties:</strong> The App and all content are
          provided on an "as is" and "as available" basis. PulseRank makes no
          representations or warranties of any kind, whether express or implied,
          regarding the App or any services. To the fullest extent permitted by
          law, PulseRank disclaims all warranties, including fitness for a
          particular purpose, accuracy, reliability, or non-infringement. We do
          not guarantee that the App will always be secure, error-free, or free
          of viruses or other harmful components.
        </p>
        <p>
          <strong>No Liability for Damages:</strong> In no event will PulseRank
          or its officers, directors, employees, or affiliates be liable for any
          direct, indirect, incidental, special, consequential or exemplary
          damages. This includes, but is not limited to, loss of profits,
          business interruption, loss of data, or personal injury, arising from
          your use of (or inability to use) the App. For example, we are not
          liable for any health, physical, or medical issues you may experience
          from using PulseRank's fitness advice. Even if PulseRank has been
          advised of the possibility of such damages, you agree that our total
          liability will be limited to the amount you (if at all) paid us in the
          past 12 months for using the App, if any. By using the App, you
          acknowledge these risks and agree that PulseRank's disclaimers are
          reasonable. Some jurisdictions do not allow the exclusion of certain
          warranties or limitations on liability, so some of the above
          disclaimers may not apply to you.
        </p>
        <p>
          <strong>Indemnification:</strong> You agree to indemnify and hold
          harmless PulseRank and its affiliates from any claims arising out of
          your violation of these Terms or the law, or your misuse of the App.
        </p>

        <h3>
          <strong>Modification of Service</strong>
        </h3>
        <p>
          PulseRank may update, modify, or discontinue (temporarily or
          permanently) the App or any features of the Service at any time. We
          will use reasonable efforts to notify users in advance of any
          significant changes (for example, by providing 48 hours' notice).
          However, PulseRank is under no obligation to maintain any specific
          feature and may introduce new features, remove existing ones, or
          change functionality. Your continued use of PulseRank after any change
          will mean you accept the modifications. We may also suspend access to
          the App for maintenance or security reasons without notice.
        </p>

        <h3>
          <strong>Governing Law and Jurisdiction</strong>
        </h3>
        <p>
          These Terms and your use of PulseRank are governed by the laws of
          India. Any dispute arising out of or relating to these Terms will be
          subject to the exclusive jurisdiction of the courts in India. For
          example, many Indian tech companies specify that disputes will be
          resolved in Indian courts. By using PulseRank, you consent to the
          personal jurisdiction of those courts over any dispute.
        </p>

        <h3>
          <strong>Other Provisions</strong>
        </h3>
        <ul>
          <li>
            <strong>Changes to Terms:</strong> We may revise these Terms from
            time to time. The revised version will be effective on the date we
            post it. We encourage you to review these Terms periodically.
            Continued use of the App after any changes means you accept the new
            terms.
          </li>
          <li>
            <strong>Severability:</strong> If any provision of these Terms is
            found invalid or unenforceable, the remaining provisions will still
            apply.
          </li>
          <li>
            <strong>Contact:</strong> If you have any questions about these
            Terms, please contact us at{" "}
            <a href="mailto:pulserank@eminds.ai">pulserank@eminds.ai</a> or the
            address below.
          </li>
        </ul>

        {/* v3_1 difference: "Company Information:" is a bold inline label, not an h3 */}
        <p>
          <strong>Company Information:</strong> PulseRank is operated by
          Enterprise Minds Inc., a corporation registered under EIN: 82-0932042.
          Address: 12667 Alcosta Boulevard, Suite 410, San Ramon, CA 94583.
          Contact email:{" "}
          <a href="mailto:pulserank@eminds.ai">pulserank@eminds.ai</a>
        </p>

        <p>
          By accessing or using the PulseRank platform, you acknowledge that you
          have read and understood this Privacy Policy.
        </p>
      </Content>
    </Container>
  );
};
export default PrivacyPolicyPage;
